// The camera collision stack's casts (engine/docs/research/suggestions/movement-camera-combat-ai.md, "camera
// collision stack"): how far the arm may reach from the pivot towards the camera's wanted position.
//
// - A ball cast from the pivot (the target) out to the wanted position; the ball's radius is the near plane's corner
//   distance plus a margin, so the near plane never cuts into a wall.
// - Each body the cast meets is classed by its thickness as a stick (`CollisionWorld.stickWidth`: the second smallest
//   extent of its colliders, so a pole or a rail is thin and a wall, thin one way only, is not). Bodies thinner than
//   `thinWidth` (poles, posts, rails) are looked past; bodies the game marks fadeable and thinner than `fadeWidth`
//   (trees, bushes, crates) are looked past and listed for the view to fade; anything else stops the arm. The class is
//   the body's, not the view's, so it can't flicker as the view line grazes a corner.
// - A guard: the ball at the arm's end must not overlap anything, a looked-past body included; if it does, the arm
//   stops where it would first touch that body. So the camera is never inside a collider.
//
// The rig adds the rest: the fast-in, slow-out spring, the predictive casts, the crane, the fades.
import type { BodyId, CollisionWorld, Hit } from "@rpg/collision";
import { CAMERA_FAULTS } from "./faults";
import { add, type Vec3 } from "./math";

/** The queries the arm needs (a `CollisionWorld` has them). */
export type ArmWorld = Pick<CollisionWorld, "castShape" | "overlap" | "stickWidth">;

/** How the arm meets the world: the game's layers and widths. */
export interface ArmOptions {
  /** The probe ball's radius (m): the near plane's corner distance plus a margin. */
  radius: number;
  /** Only these layers stop or fade the camera (default every layer). */
  layers?: readonly string[];
  /** The followed body, never hit (the arm starts inside it). */
  exclude?: BodyId;
  /** Bodies thinner than this as a stick are looked past (m). */
  thinWidth: number;
  /** Fadeable bodies thinner than this as a stick are looked past and faded (m). */
  fadeWidth: number;
  /** Which bodies may fade instead of moving the camera (by ID and surface tag). Default none. */
  canFade?: (id: BodyId, tag: string | null) => boolean;
  /** Bodies looked past in one cast before giving up and stopping at the next (default 6). */
  maxSkips?: number;
}

/** How the arm met the world along one line. */
export interface ArmSolve {
  /** How far the ball can travel from the pivot towards the wanted position (m, ≤ the wanted length). */
  allowed: number;
  /** The body that stopped it, or null. */
  blocker: BodyId | null;
  /** Thin bodies looked past. */
  thin: BodyId[];
  /** Fadeable bodies looked past (the view fades them). */
  faded: BodyId[];
  /** A looked-past body the guard stopped the arm at, or null. */
  guarded: BodyId | null;
  /** Queries made (casts and overlaps), for the cost. */
  casts: number;
}

const IDENTITY = { x: 0, y: 0, z: 0, w: 1 } as const;

/**
 * How far the arm reaches from `pivot` along the unit direction `dir`, up to `want` metres: thin bodies looked past,
 * fadeable small bodies looked past and listed, the camera never left inside anything.
 */
export function solveArm(world: ArmWorld | null, pivot: Vec3, dir: Vec3, want: number, o: ArmOptions): ArmSolve {
  const out: ArmSolve = { allowed: want, blocker: null, thin: [], faded: [], guarded: null, casts: 0 };
  if (!world || CAMERA_FAULTS.noCollision || want <= 0) return out;
  const ball = { type: "ball" as const, radius: o.radius };
  const skipped = new Set<BodyId>();
  const keep = (b: BodyId): boolean => !skipped.has(b);
  const maxSkips = o.maxSkips ?? 6;
  for (let i = 0; i <= maxSkips; i++) {
    out.casts++;
    const hit: Hit | null = world.castShape(ball, pivot, IDENTITY, dir, want, { layers: o.layers, exclude: o.exclude, filter: keep });
    if (!hit) break;
    if (i < maxSkips) {
      const width = world.stickWidth(hit.id);
      if (width < o.thinWidth && !CAMERA_FAULTS.thinAsSolid) {
        out.thin.push(hit.id);
        skipped.add(hit.id);
        continue;
      }
      if (width < o.fadeWidth && o.canFade?.(hit.id, hit.tag)) {
        out.faded.push(hit.id);
        skipped.add(hit.id);
        continue;
      }
    }
    out.allowed = Math.max(0, Math.min(want, hit.distance));
    out.blocker = hit.id;
    break;
  }
  // the guard: the ball at the arm's end must not sit inside a body the cast looked past
  if (skipped.size > 0) {
    for (let i = 0; i < skipped.size; i++) {
      out.casts++;
      const inside = world.overlap(ball, add(pivot, dir, out.allowed), IDENTITY, { layers: o.layers, exclude: o.exclude, filter: (b) => skipped.has(b) });
      if (inside.length === 0) break;
      let nearest = out.allowed;
      for (const id of inside) {
        out.casts++;
        const h = world.castShape(ball, pivot, IDENTITY, dir, want, { layers: o.layers, filter: (b) => b === id });
        nearest = Math.min(nearest, h ? h.distance : 0);
        out.guarded = id;
      }
      out.allowed = Math.max(0, nearest);
    }
  }
  return out;
}

/** The distance from the eye to a corner of the near plane: the smallest ball that keeps the near plane out of walls. */
export function nearCornerDistance(near: number, fovDeg: number, aspect: number): number {
  const h = near * Math.tan((fovDeg * Math.PI) / 360);
  return Math.hypot(near, h, h * aspect);
}
